import { type BranchKey, usePushBranchToBaseBranch } from '@festina-life/api'
import { Alert } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { Popover, PopoverButton } from '@festina-life/ui/structure'

import { useLayoutContext } from '../../../../context/layoutContext'
import { ActionsMenuPanel } from '../../../actionsPanel/ActionsMenuPanel'
import { OperationsLoadedButton } from '../../../operationsButton/OperationsLoadedButton'
import { PushToBaseBranchButton } from './PushToBaseBranchButton'
import { PushUnavailabilityTooltip } from './PushUnavailabilityTooltip'
import { usePushToBaseBranchAvailability } from './usePushToBaseBranchAvailability'

type Props = {
  branchKey: BranchKey
}

export const PushToBaseBranchPersonaAwareButton = ({ branchKey }: Props) => {
  const translate = useTranslate()
  const { persona } = useLayoutContext()
  const { isPushAvailable, isPushVisible, unavailableReasons } =
    usePushToBaseBranchAvailability(branchKey)
  const { mutateAsync: pushBranchToBaseBranch, isPending: isPushingBranch } =
    usePushBranchToBaseBranch()

  if (!isPushVisible) {
    return null
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {persona !== 'ConfigurationAdmin' ? (
        <Popover>
          <PopoverButton as="div">
            <OperationsLoadedButton
              startIcon="ChevronDoubleRight"
              disabled={!isPushAvailable}
              tip={
                unavailableReasons.length ? (
                  <PushUnavailabilityTooltip
                    unavailableReasons={unavailableReasons}
                  />
                ) : undefined
              }
            >
              {translate('branchDetailsPanel.tabs.manage.pushToBaseBranch')}
            </OperationsLoadedButton>
          </PopoverButton>
          <ActionsMenuPanel
            anchor={{ to: 'right' }}
            className="max-w-lg! flex flex-col items-end gap-y-2"
          >
            <Alert severity="warning">
              {translate(
                'branchDetailsPanel.tabs.manage.pushToBaseBranch.warning',
              )}
            </Alert>
            <PushToBaseBranchButton
              branchKey={branchKey}
              isPushEnabled={isPushAvailable}
              label={translate(
                'branchDetailsPanel.tabs.manage.pushToBaseBranchConfirmation',
              )}
              variant="primary"
              isShowingIcon={false}
              onPush={pushBranchToBaseBranch}
              isPending={isPushingBranch}
              unavailableReasons={unavailableReasons}
            />
          </ActionsMenuPanel>
        </Popover>
      ) : (
        <PushToBaseBranchButton
          isShowingIcon
          branchKey={branchKey}
          isPushEnabled={isPushAvailable}
          label={translate('branchDetailsPanel.tabs.manage.pushToBaseBranch')}
          variant="secondary"
          onPush={pushBranchToBaseBranch}
          isPending={isPushingBranch}
          unavailableReasons={unavailableReasons}
        />
      )}
    </div>
  )
}
